import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import TimeScrollPicker from './TimeScrollPicker';

const TimeInputModal = ({ 
  isOpen, 
  onClose, 
  initialHour = '09', 
  initialMinute = '00', 
  initialAmPm = 'AM', 
  onTimeSelected,
  label = 'Select Time' 
}) => {
  const [selectedHour, setSelectedHour] = useState(initialHour);
  const [selectedMinute, setSelectedMinute] = useState(initialMinute);
  const [selectedAmPm, setSelectedAmPm] = useState(initialAmPm);
  
  useEffect(() => {
    // Update internal state when props change
    setSelectedHour(initialHour);
    setSelectedMinute(initialMinute);
    setSelectedAmPm(initialAmPm);
  }, [initialHour, initialMinute, initialAmPm]);
  
  const handleTimeChange = ({ hour, minute, amPm }) => {
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setSelectedAmPm(amPm);
  };
  
  const handleConfirm = () => {
    if (onTimeSelected) {
      onTimeSelected({
        hour: selectedHour,
        minute: selectedMinute,
        amPm: selectedAmPm,
        formattedTime: `${selectedHour}:${selectedMinute} ${selectedAmPm}`
      });
    }
    onClose();
  };
  
  if (!isOpen) return null;
  
  // Create a portal to render the modal at the document body level
  const modalContent = (
    <div className="time-modal-overlay">
      <style>
        {`
          .time-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            animation: fadeIn 0.2s ease;
            pointer-events: auto;
          }
          
          .time-modal {
            background-color: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
            width: 90%;
            max-width: 320px;
            overflow: hidden;
            position: fixed;
            top: 35%;  /* Moved up from 40% to 35% for better visibility */
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 10000;
            animation: slideUp 0.3s ease;
          }
          
          @media (max-width: 768px) {
            .time-modal {
              top: 30%; /* Even higher positioning on mobile */
              max-height: 80vh; /* Limit height on mobile */
            }
          }
          
          .time-modal-header {
            padding: 16px;
            background-color: #2A9D8F;
            border-bottom: 1px solid #eee;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .time-modal-title {
            font-size: 16px;
            font-weight: 600;
            color: white;
            margin: 0;
          }
          
          .time-modal-close {
            background: none;
            border: none;
            font-size: 20px;
            line-height: 1;
            cursor: pointer;
            color: white;
          }
          
          .time-modal-content {
            padding: 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          
          .column-labels {
            display: flex;
            width: 220px;
            justify-content: space-between;
            margin-bottom: 0;
            margin-top: 15px;
          }
          
          .column-label {
            flex: 1;
            text-align: center;
            font-size: 12px;
            font-weight: 500;
            color: #666;
          }
          
          .time-display {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 20px;
            color: #2A9D8F;
            text-align: center;
            padding: 5px 12px;
            border-radius: 8px;
            background-color: #f8f9fa;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
          }
          
          .time-display-separator {
            opacity: 0.6;
            margin: 0 2px;
          }
          
          .time-display-period {
            font-size: 20px;
            color: #2A9D8F;
            margin-left: 6px;
          }
          
          .time-modal-footer {
            padding: 16px;
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            border-top: 1px solid #eee;
          }
          
          .time-modal-button {
            padding: 8px 16px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
          }
          
          .time-modal-button.cancel {
            background-color: #f8f9fa;
            color: #666;
          }
          
          .time-modal-button.confirm {
            background-color: #2A9D8F;
            color: white;
          }
          
          .time-modal-button:hover {
            opacity: 0.9;
            transform: translateY(-1px);
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { transform: translate(-50%, -30%); opacity: 0; }
            to { transform: translate(-50%, -50%); opacity: 1; }
          }
        `}
      </style>
      
      <div className="time-modal">
        <div className="time-modal-header">
          <h3 className="time-modal-title">{label}</h3>
          <button className="time-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="time-modal-content">
          <div className="time-display">
            <span>{selectedHour}</span>
            <span className="time-display-separator">:</span>
            <span>{selectedMinute}</span>
            <span className="time-display-period">{selectedAmPm}</span>
          </div>
          
          <div className="column-labels">
            <div className="column-label">Hour</div>
            <div className="column-label">Minute</div>
            <div className="column-label">Period</div>
          </div>
          
          <TimeScrollPicker
            initialHour={selectedHour}
            initialMinute={selectedMinute}
            initialAmPm={selectedAmPm}
            onTimeChange={handleTimeChange}
          />
        </div>
        
        <div className="time-modal-footer">
          <button className="time-modal-button cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="time-modal-button confirm" onClick={handleConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
  
  // Try to render to our dedicated root if it exists, otherwise fall back to document.body
  const portalTarget = document.getElementById('time-picker-root') || document.body;
  
  // Use React Portal to render at document body level
  return ReactDOM.createPortal(
    modalContent,
    portalTarget
  );
};

export default TimeInputModal;